import type { Booking, Expense, Production } from '__types__';
import type { BookingApi } from 'v5/api/booking/booking.api';
import { extractBookingIdsFromProduction } from 'v5/api/production-resourcing/extractBookingIdsFromProduction';
import {
  mapV1ProductionToBillingTree,
  primePayloadFromV1,
  type ProductionResourcingV1CachePrime
} from 'v5/api/production-resourcing/mapProductionResourcingFromV1.http';
import type { ResourcingBillingTree } from 'v5/core/production-resourcing/billing-tree';
import type {
  EnsureProduction,
  ProductionResourcingApi
} from './productionResourcing.api';

function expensesByBookingIdFromApi(
  bookingIds: number[],
  records: Array<{ booking_id: number; expenses: Expense[] }>
): Record<number, Expense[]> {
  const map: Record<number, Expense[]> = {};
  for (const id of bookingIds) {
    map[id] = [];
  }
  for (const { booking_id, expenses } of records) {
    map[booking_id] = expenses;
  }
  return map;
}

async function fetchBookingsAndExpenses(
  bookingApi: BookingApi,
  bookingIds: number[]
): Promise<{
  bookings: Awaited<ReturnType<BookingApi['getItems']>>;
  expensesByBookingId: Record<number, Expense[]>;
}> {
  if (bookingIds.length === 0) {
    return { bookings: [], expensesByBookingId: {} };
  }
  const [bookings, expenseRecords] = await Promise.all([
    bookingApi.getItems(bookingIds),
    bookingApi.getBookingExpenses(bookingIds)
  ]);
  return {
    bookings,
    expensesByBookingId: expensesByBookingIdFromApi(bookingIds, expenseRecords)
  };
}

export type ProductionResourcingCachePrime = ProductionResourcingV1CachePrime;

export function primeLegacyCachesFromV1(
  queryClient: {
    setQueryData: (key: readonly unknown[], updater: unknown) => unknown;
  },
  productionDetailQueryKey: (id: number) => readonly unknown[],
  prime: ProductionResourcingCachePrime
): void {
  queryClient.setQueryData(
    productionDetailQueryKey(prime.production.id ?? 0),
    prime.production
  );
  for (const booking of Object.values(prime.bookingsById)) {
    queryClient.setQueryData(['booking', 'detail', booking.id], booking);
  }
}

async function buildBillingTreesFromProductions(
  bookingApi: BookingApi,
  productions: Production[]
): Promise<{
  trees: Record<number, ResourcingBillingTree>;
  primes: Record<number, ProductionResourcingCachePrime>;
}> {
  if (productions.length === 0) {
    return { trees: {}, primes: {} };
  }

  const allBookingIds = new Set<number>();
  for (const production of productions) {
    for (const bookingId of extractBookingIdsFromProduction(production)) {
      allBookingIds.add(bookingId);
    }
  }
  const bookingIds = Array.from(allBookingIds);
  const { bookings, expensesByBookingId } = await fetchBookingsAndExpenses(
    bookingApi,
    bookingIds
  );
  const bookingsById = Object.fromEntries(
    bookings.map(booking => [booking.id, booking])
  );

  const trees: Record<number, ResourcingBillingTree> = {};
  const primes: Record<number, ProductionResourcingCachePrime> = {};

  for (const production of productions) {
    const productionBookingIds = extractBookingIdsFromProduction(production);
    const productionBookings = productionBookingIds
      .map(id => bookingsById[id])
      .filter((booking): booking is NonNullable<typeof booking> =>
        Boolean(booking)
      );
    const productionExpenses: Record<number, Expense[]> = {};
    for (const id of productionBookingIds) {
      productionExpenses[id] = expensesByBookingId[id] ?? [];
    }
    trees[production.id] = mapV1ProductionToBillingTree(
      production,
      productionBookings,
      productionExpenses
    );
    primes[production.id] = primePayloadFromV1(production, productionBookings);
  }

  return { trees, primes };
}

export function createProductionResourcingApi(config: {
  bookingApi: BookingApi;
  ensureProduction: EnsureProduction;
}): ProductionResourcingApi {
  const { bookingApi, ensureProduction } = config;

  return {
    getBillingTreesByIds: async productionIds => {
      if (productionIds.length === 0) {
        return { trees: {}, primes: {} };
      }
      const productions = await Promise.all(
        productionIds.map(id => ensureProduction(id))
      );
      return buildBillingTreesFromProductions(bookingApi, productions);
    }
  };
}
